/**
 * StripeProvider - Implémentation du provider Stripe
 *
 * Ce provider gère les paiements via Stripe Elements
 */

import { loadStripe } from '@stripe/stripe-js';
import { BasePaymentProvider } from './BaseProvider';

export class StripeProvider extends BasePaymentProvider {
  constructor(config) {
    super({
      id: 'stripe',
      name: 'Stripe',
      icon: '/icons/payment/stripe.svg',
      ...config
    });

    this.publishableKey = config.publishableKey;
    this.secretKey = config.secretKey;
    this.stripe = null;
    this.elements = null;
    this.cardElement = null;
  }

  /**
   * Initialise Stripe SDK
   * @returns {Promise<any>} Instance Stripe
   */
  async initialize() {
    if (!this.publishableKey) {
      throw new Error('Stripe publishable key is required');
    }

    this.stripe = await loadStripe(this.publishableKey);

    if (!this.stripe) {
      throw new Error('Failed to load Stripe SDK');
    }

    return this.stripe;
  }

  /**
   * Crée un PaymentIntent via l'API backend
   * @param {number} amount - Montant en cents
   * @param {string} currency - Devise (USD, EUR, etc.)
   * @param {object} metadata - Métadonnées
   * @returns {Promise<object>} Response avec clientSecret
   */
  async createPayment(amount, currency, metadata) {
    const response = await fetch('/api/payment/stripe/create-intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        amount,
        currency: currency.toLowerCase(),
        metadata
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create PaymentIntent');
    }

    return response.json();
  }

  /**
   * Confirme le paiement avec les détails de la carte
   * @param {object} paymentData - Données du paiement
   * @param {string} paymentData.clientSecret - Client secret du PaymentIntent
   * @param {object} paymentData.cardElement - Element Stripe de la carte
   * @param {string} paymentData.cardHolder - Nom du titulaire de la carte
   * @returns {Promise<object>} PaymentIntent confirmé
   */
  async processPayment(paymentData) {
    const { clientSecret, cardElement, cardHolder } = paymentData;

    if (!this.stripe) {
      throw new Error('Stripe not initialized. Call initialize() first.');
    }

    if (!cardElement) {
      throw new Error('Card element is required');
    }

    const { error, paymentIntent } = await this.stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: cardHolder || 'Anonymous'
          }
        }
      }
    );

    if (error) {
      throw new Error(error.message);
    }

    return paymentIntent;
  }

  /**
   * Monte l'élément de carte Stripe dans le DOM
   * @param {string} containerId - ID du conteneur DOM
   * @param {string} clientSecret - Client secret du PaymentIntent
   * @param {object} appearance - Configuration d'apparence
   * @returns {object} Card element créé
   */
  mountCardElement(containerId, clientSecret, appearance = {}) {
    if (!this.stripe) {
      throw new Error('Stripe not initialized. Call initialize() first.');
    }

    // Créer les Elements avec le client secret
    this.elements = this.stripe.elements({
      clientSecret,
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#9333ea',
          colorBackground: '#ffffff',
          colorText: '#1a1a1a',
          ...appearance.variables
        }
      }
    });

    // Créer l'élément de carte
    this.cardElement = this.elements.create('payment', {
      fields: {
        billingDetails: {
          name: 'auto',
          email: 'never'
        }
      }
    });

    // Monter dans le DOM
    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error(`Container #${containerId} not found`);
    }

    this.cardElement.mount(`#${containerId}`);

    // Écouter les changements de validation
    this.cardElement.on('change', (event) => {
      const errorDiv = document.getElementById('card-errors');
      if (errorDiv) {
        if (event.error) {
          errorDiv.textContent = event.error.message;
          errorDiv.style.display = 'block';
        } else {
          errorDiv.style.display = 'none';
        }
      }
    });

    return this.cardElement;
  }

  /**
   * Formate le montant pour Stripe
   * Stripe ne supporte pas XOF/FCFA, donc on convertit vers USD
   * @param {number} amount - Montant original en cents
   * @param {string} fromCurrency - Devise source
   * @returns {number} Montant en cents pour Stripe
   */
  formatAmount(amount, fromCurrency) {
    // Stripe supporte ces devises sans conversion
    const SUPPORTED_CURRENCIES = [
      'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'NOK',
      'SEK', 'DKK', 'PLN', 'HUF', 'CZK', 'ILS', 'MXN', 'BRL', 'PHP'
    ];

    if (SUPPORTED_CURRENCIES.includes(fromCurrency.toUpperCase())) {
      return amount;
    }

    // Convertir XOF/FCFA vers USD
    const CONVERSION_RATES = {
      'XOF': 0.0016,
      'FCFA': 0.0016
    };

    const rate = CONVERSION_RATES[fromCurrency.toUpperCase()] || 0.0016;
    return Math.round(amount * rate * 100);
  }

  /**
   * Détruit les éléments Stripe
   */
  destroy() {
    if (this.cardElement) {
      this.cardElement.destroy();
      this.cardElement = null;
    }
    if (this.elements) {
      this.elements = null;
    }
    this.stripe = null;
  }
}
